import {
  DocumentTemplateType,
  DocumentTemplateRuleDto,
  DocumentTemplateRuleListDto,
  QueryFactory,
  TemplateListDto,
} from '@lmt/shared-types';
import {
  DXG,
  DataGrid,
  useDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { AutoSizedTableWrapper } from '@lmt/unicorn-shared/app/components/common/AutoSizedTableWrapper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useEventCallback } from '@mui/material';
import { DataChange } from 'devextreme/common/grids';
import React, { FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  subtenantId?: number;
};

const nameof = nameofFactory<DocumentTemplateRuleDto>();

export const TemplatesRulesTable: FC<Props> = (props) => {
  const i18n = useScopedTranslation('patients.document_template_rules');
  const i18nextGlobal = useTranslation();
  const modals = useModal();
  useDataGrid();
  const rulesQuery = QueryFactory.DocumentTemplateRuleQuery.useGetQuery(
    {
      subtenantId: props.subtenantId,
    },
    {
      enabled: !!props.subtenantId,
    },
  );

  const dataGridRef =
    useRef<DataGrid<DocumentTemplateRuleListDto, number>>(null);
  const refetchQuery = async () => {
    const state = dataGridRef.current?.instance().state();
    await rulesQuery.refetch();
    dataGridRef.current?.instance().state(state);
  };
  const templateRules = useMemo(() => {
    return rulesQuery.data ?? [];
  }, [rulesQuery.data]);

  const { data: documentTemplates } =
    QueryFactory.DocumentTemplateQuery.useGetQuery({
      select(data) {
        const result = data.map((item) => {
          return {
            ...item,
            localized: item.isDefault
              ? i18nextGlobal.t(item.title as any)
              : item.title,
          };
        });
        result.sort((a, b) =>
          a.localized.toLowerCase() > b.localized.toLowerCase() ? 1 : -1,
        );
        return result;
      },
    });

  const onRowUpdating = async (
    id: number,
    data: Partial<DocumentTemplateRuleDto>,
  ) => {
    await QueryFactory.DocumentTemplateRuleClient.patch(id, data);
  };

  useEffect(
    function createMissingRules() {
      if (!rulesQuery.isFetched) {
        return;
      }
      const templatesWithoutRules =
        documentTemplates?.filter(
          (template) =>
            template.type === DocumentTemplateType.Document &&
            !templateRules.find((rule) => rule.templateId === template.id),
        ) ?? [];
      if (templatesWithoutRules.length) {
        createRulesForTemplates(templatesWithoutRules).catch((e) =>
          console.log(e),
        );
      }
    },
    [templateRules],
  );

  const createRulesForTemplates = useEventCallback(
    async (templates: TemplateListDto[]) => {
      try {
        for (const template of templates) {
          await QueryFactory.DocumentTemplateRuleClient.create({
            isActive: true,
            isOpeningAfterSaving: false,
            templateId: template.id,
            subtenantId: props.subtenantId!,
          });
        }
      } finally {
        await refetchQuery();
      }
    },
  );

  return (
    <Loading loading={rulesQuery.isLoading} title={i18n.t('loading')}>
      <AutoSizedTableWrapper>
        <DXG.DataGrid
          ref={dataGridRef}
          onSaving={(props) => {
            props.promise = (async () => {
              try {
                for (const change of props.changes as DataChange<
                  DocumentTemplateRuleListDto,
                  number
                >[]) {
                  switch (change.type) {
                    case 'update':
                      await onRowUpdating(change.key, change.data);
                      break;
                    case 'remove':
                    case 'insert':
                      break;
                  }
                }
              } catch (e) {
                void modals.showError({ text: convertToErrorString(e) });
                props.cancel = true;
              }
            })();
          }}
          dataSource={templateRules}
          keyExpr={'id'}
          showBorders={true}
          onInitNewRow={(e) => {
            const data = e.data;
            data.isActive = true;
            data.isOpeningAfterSaving = false;
            data.subtenantId = props.subtenantId!;
            data.id = 10000000 + Math.round(Math.random() * 10000);
          }}
          style={{ width: '100%' }}
        >
          <DXG.Paging enabled={false} />
          <DXG.Editing
            mode="batch"
            allowUpdating={true}
            selectTextOnEditStart={true}
            startEditAction="click"
          />
          <DXG.Column dataField="id" sortOrder="desc" visible={false} />
          <DXG.Column
            width={120}
            dataField={nameof('isActive')}
            dataType="boolean"
            caption={i18n.t('columns.is_active')}
          />
          <DXG.Column
            width={190}
            dataField={nameof('isOpeningAfterSaving')}
            dataType="boolean"
            caption={i18n.t('columns.open_after_saving')}
          />
          <DXG.Column
            dataField={nameof('templateId')}
            alignment={'left'}
            allowEditing={false}
            calculateDisplayValue={(item: DocumentTemplateRuleListDto) => {
              const template = documentTemplates?.find(
                (x) => x.id === item.templateId,
              );
              return template?.isDefault
                ? i18nextGlobal.t(template.title as any)
                : template?.title;
            }}
            caption={i18n.t('columns.template')}
            dataType="number"
          />
          <DXG.Column
            dataField={nameof('headerId')}
            caption={i18n.t('columns.header')}
            dataType="number"
          >
            <DXG.Lookup
              dataSource={documentTemplates?.filter(
                (x) => x.type === DocumentTemplateType.DocumentHeader,
              )}
              displayExpr={'localized'}
              valueExpr="id"
              allowClearing={true}
            />
          </DXG.Column>
          <DXG.Column
            dataField={nameof('firstPageHeaderId')}
            caption={i18n.t('columns.first_page_header')}
            dataType="number"
          >
            <DXG.Lookup
              dataSource={documentTemplates?.filter(
                (x) => x.type === DocumentTemplateType.DocumentHeader,
              )}
              displayExpr={(item: TemplateListDto) =>
                item.isDefault ? i18nextGlobal.t(item.title as any) : item.title
              }
              valueExpr="id"
              allowClearing={true}
            />
          </DXG.Column>
          <DXG.Column
            dataField={nameof('footerId')}
            caption={i18n.t('columns.footer')}
            dataType="number"
          >
            <DXG.Lookup
              dataSource={documentTemplates?.filter(
                (x) => x.type === DocumentTemplateType.DocumentFooter,
              )}
              displayExpr={(item: TemplateListDto) =>
                item.isDefault ? i18nextGlobal.t(item.title as any) : item.title
              }
              valueExpr="id"
              allowClearing={true}
            />
          </DXG.Column>
          <DXG.Column
            dataField={nameof('firstPageFooterId')}
            caption={i18n.t('columns.first_page_footer')}
            dataType="number"
          >
            <DXG.Lookup
              dataSource={documentTemplates?.filter(
                (x) => x.type === DocumentTemplateType.DocumentFooter,
              )}
              displayExpr={(item: TemplateListDto) =>
                item.isDefault ? i18nextGlobal.t(item.title as any) : item.title
              }
              valueExpr="id"
              allowClearing={true}
            />
          </DXG.Column>
        </DXG.DataGrid>
      </AutoSizedTableWrapper>
    </Loading>
  );
};
